import {
  type AnalysisTarget,
  parseAnalysisTarget,
} from "@/lib/ai/analysis-target";
import {
  type AnalysisTargetPathContext,
  buildAnalysisTargetNavigationPath,
} from "@/lib/ai/analysis-target-navigation";

const CURRENT_PAGE_KINDS = [
  "iw_project_board",
  "iw_project_list",
  "iw_project_settings",
  "iw_detail",
  "sa_project_board",
  "sa_project_list",
  "sa_project_default",
  "sa_project_settings",
  "sa_view_detail",
  "ua_project_board",
  "ua_project_list",
  "ua_project_default",
  "ua_project_settings",
  "ua_view_detail",
  "ua_session_explorer",
] as const;

export type CurrentPageKind = (typeof CURRENT_PAGE_KINDS)[number];

type CurrentPageTargetKind = "project" | "view" | "index-worker";

type CurrentPageRouteContract = {
  projectType: AnalysisTarget["projectType"];
  targetKind: CurrentPageTargetKind;
  routeSegment: string | null;
  analysisScopeEligible: boolean;
};

const CURRENT_PAGE_ROUTE_CONTRACTS = {
  iw_project_board: {
    projectType: "IndexWorkerProject",
    targetKind: "project",
    routeSegment: "board",
    analysisScopeEligible: true,
  },
  iw_project_list: {
    projectType: "IndexWorkerProject",
    targetKind: "project",
    routeSegment: "list",
    analysisScopeEligible: true,
  },
  iw_project_settings: {
    projectType: "IndexWorkerProject",
    targetKind: "project",
    routeSegment: "settings",
    analysisScopeEligible: false,
  },
  iw_detail: {
    projectType: "IndexWorkerProject",
    targetKind: "index-worker",
    routeSegment: null,
    analysisScopeEligible: true,
  },
  sa_project_board: {
    projectType: "SearchAnalyticsProject",
    targetKind: "project",
    routeSegment: "board",
    analysisScopeEligible: true,
  },
  sa_project_list: {
    projectType: "SearchAnalyticsProject",
    targetKind: "project",
    routeSegment: "list",
    analysisScopeEligible: true,
  },
  sa_project_default: {
    projectType: "SearchAnalyticsProject",
    targetKind: "project",
    routeSegment: "default",
    analysisScopeEligible: true,
  },
  sa_project_settings: {
    projectType: "SearchAnalyticsProject",
    targetKind: "project",
    routeSegment: "settings",
    analysisScopeEligible: false,
  },
  sa_view_detail: {
    projectType: "SearchAnalyticsProject",
    targetKind: "view",
    routeSegment: null,
    analysisScopeEligible: true,
  },
  ua_project_board: {
    projectType: "UserAnalyticsProject",
    targetKind: "project",
    routeSegment: "board",
    analysisScopeEligible: true,
  },
  ua_project_list: {
    projectType: "UserAnalyticsProject",
    targetKind: "project",
    routeSegment: "list",
    analysisScopeEligible: true,
  },
  ua_project_default: {
    projectType: "UserAnalyticsProject",
    targetKind: "project",
    routeSegment: "default",
    analysisScopeEligible: true,
  },
  ua_project_settings: {
    projectType: "UserAnalyticsProject",
    targetKind: "project",
    routeSegment: "settings",
    analysisScopeEligible: false,
  },
  ua_view_detail: {
    projectType: "UserAnalyticsProject",
    targetKind: "view",
    routeSegment: null,
    analysisScopeEligible: true,
  },
  ua_session_explorer: {
    projectType: "UserAnalyticsProject",
    targetKind: "project",
    routeSegment: "session_explorer",
    analysisScopeEligible: false,
  },
} as const satisfies Record<CurrentPageKind, CurrentPageRouteContract>;

/**
 * AI が「今どの画面を見ているか」を判断するための構造化コンテキスト。
 *
 * `analysisScope` と違って、この値は tool planning の優先対象を決めるためではなく、
 * 現在表示中の画面種別と、その画面に紐づく target を説明するために使う。
 */
export type CurrentPageContext = {
  pageKind: CurrentPageKind;
  target: AnalysisTarget | null;
  /** Session Explorer の URLSearchParams.get() 値。追加の decode はせず、リンク生成時に再エンコードする。 */
  sessionQuery?: { gaSessionId?: string; userPseudoId?: string };
};

/**
 * 表示中ページが分析対象 chip を持てるかを返す。
 *
 * settings / session explorer は分析対象ではないが、現在地としては AI へ渡す。
 * 分析対象ページで chip が外された場合だけ、現在ページコンテキストも送らない。
 */
export function isCurrentPageAnalysisScopeEligible(
  pageKind: CurrentPageKind,
): boolean {
  return CURRENT_PAGE_ROUTE_CONTRACTS[pageKind].analysisScopeEligible;
}

function targetKindOf(target: AnalysisTarget): CurrentPageTargetKind {
  if (target.iwId) return "index-worker";
  if (target.viewId) return "view";
  return "project";
}

function assertCurrentPageTargetMatchesKind(
  pageKind: CurrentPageKind,
  target: AnalysisTarget,
): void {
  const contract = CURRENT_PAGE_ROUTE_CONTRACTS[pageKind];
  if (
    target.projectType !== contract.projectType ||
    targetKindOf(target) !== contract.targetKind
  ) {
    throw new Error(
      `currentPageContext.target is incompatible with pageKind ${pageKind}.`,
    );
  }
}

/**
 * current page context が表す表示中ページの canonical path を返す。
 * 分析対象の project path と異なり、board / list / settings などの page kind を保持する。
 * `target` がない場合は `null`、`pageKind` と target の domain・detail 種別が合わない場合は例外を返す。
 */
export function buildCurrentPageNavigationPath(
  currentPageContext: CurrentPageContext,
  navigationContext: AnalysisTargetPathContext,
): string | null {
  const { pageKind, target } = currentPageContext;
  if (!target) return null;
  assertCurrentPageTargetMatchesKind(pageKind, target);

  const targetPath = buildAnalysisTargetNavigationPath(
    target,
    navigationContext,
  );
  const routeSegment = CURRENT_PAGE_ROUTE_CONTRACTS[pageKind].routeSegment;
  const path = routeSegment ? `${targetPath}/${routeSegment}` : targetPath;
  if (pageKind !== "ua_session_explorer" || !currentPageContext.sessionQuery) {
    return path;
  }
  const query = new URLSearchParams(currentPageContext.sessionQuery).toString();
  return query ? `${path}?${query}` : path;
}

function parseCurrentPageKind(value: unknown): CurrentPageKind {
  if (
    typeof value !== "string" ||
    !CURRENT_PAGE_KINDS.includes(value as CurrentPageKind)
  ) {
    throw new Error("currentPageContext.pageKind is invalid.");
  }
  return value as CurrentPageKind;
}

/**
 * リクエストボディの currentPageContext を厳格に検証し、正規化する。
 *
 * 失敗時:
 * - 期待スキーマに合わない場合は例外を投げる。
 */
export function parseCurrentPageContext(
  raw: unknown,
): CurrentPageContext | null {
  if (raw === null || raw === undefined) {
    return null;
  }
  if (typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error("currentPageContext must be an object.");
  }

  const record = raw as Record<string, unknown>;
  const pageKind = parseCurrentPageKind(record.pageKind);
  const target = parseAnalysisTarget(record.target);
  if (target) assertCurrentPageTargetMatchesKind(pageKind, target);
  let sessionQuery: CurrentPageContext["sessionQuery"];
  if (record.sessionQuery !== undefined) {
    if (
      pageKind !== "ua_session_explorer" ||
      record.sessionQuery === null ||
      typeof record.sessionQuery !== "object" ||
      Array.isArray(record.sessionQuery)
    ) {
      throw new Error("currentPageContext.sessionQuery is invalid.");
    }
    sessionQuery = {};
    for (const [key, value] of Object.entries(record.sessionQuery)) {
      if (
        (key !== "gaSessionId" && key !== "userPseudoId") ||
        typeof value !== "string"
      ) {
        throw new Error("currentPageContext.sessionQuery is invalid.");
      }
      sessionQuery[key] = value;
    }
  }
  return {
    pageKind,
    target,
    ...(sessionQuery ? { sessionQuery } : {}),
  } satisfies CurrentPageContext;
}
